import { buildAbilityFor, logOff, sessionCache } from "modules/auth";
import { NotificationSubscriptionCategory, TimeSubscription } from "modules/common";
import { mapUserAccount, mapUserProfile } from "modules/dashboard";
import { toast } from "react-toastify";
import {
    activateUser,
    deleteMyAccount,
    getAllCounties,
    getEducationTimeSubscription as getEducationTimeSub,
    getModuleManagementData as getModuleManagement,
    getModuleUsageDashboardData,
    getMyAccount,
    getNotificationSubscriptionsForCategory as getSubscriptionsForCategory,
    getUserAccount,
    getUserAccountDashboardData,
    updateEducationTimeSubscription as updateEducationTimeSub,
    updateModuleManagement as updateModule,
    updateMyAccountMetadata,
    UpdateMobileNumberRequest,
    UpdateMyAccountMetadataRequest,
    updateMyAccountMobileNumber,
    updateMyAccountSettings,
    updateRoles,
    updateNotificationSubscription as updateSubscription,
} from "services";
import { AppThunk } from "store";
import {
    activateUserError,
    activateUserRequest,
    getDashboardDataRequest,
    getUserAccountError,
    getUserAccountRequest,
    getUserAccountSuccess,
    updateRolesRequest,
} from ".";
import { getDashboardDataError, getDashboardDataSuccess } from "./accountDashboardDataSlice";
import { activateUserSuccess } from "./activateUserSlice";
import { getCountiesError, getCountiesRequest, getCountiesSuccess } from "./addressSlice";
import {
    deactivateAccountError,
    deactivateAccountRequest,
    deactivateAccountSuccess,
} from "./deactivateAccountSlice";
import {
    getModuleUsageDataError,
    getModuleUsageDataRequest,
    getModuleUsageDataSuccess,
} from "./moduleUsageDataSlice";
import {
    getProfileError,
    getProfileRequest,
    getProfileSuccess,
    updateModuleManagementSuccess,
    updateProfileError,
    updateProfileRequest,
    updateProfileSuccess,
} from "./profileSlice";
import { updateRolesError, updateRolesSuccess } from "./updateUserRoleSlice";
import { getExpertAreas, getMeasures } from "modules/education/store";

// TODO: use useQuery
export const getUserAccountData =
    (uid: string, roleType: number): AppThunk =>
    async (dispatch, getState) => {
        try {
            const state = getState();

            if (state.signIn.user) {
                dispatch(getUserAccountRequest());
                const { accessToken } = state.signIn.user;
                const response = await getUserAccount(uid, roleType, accessToken);

                dispatch(getUserAccountSuccess({ user: mapUserAccount(response.data.account) }));
            }
        } catch (error: any) {
            const [first] = error.response.data.statuses;
            dispatch(getUserAccountError({ errorMessage: first.message }));
        }
    };

export const getCounties = (): AppThunk => async (dispatch, getState) => {
    try {
        const { address } = getState();

        if (!address.counties.length) {
            dispatch(getCountiesRequest());
            const response = await getAllCounties();

            dispatch(getCountiesSuccess({ counties: response.data.counties }));
        }
    } catch (error: any) {
        dispatch(getCountiesError({ errorMessage: error }));
    }
};

export const deactivateUserByUid =
    (uid: string, roleType: number, isDeactivate: boolean): AppThunk =>
    async (dispatch, getState) => {
        try {
            const state = getState();

            dispatch(activateUserRequest());

            if (state.signIn.user) {
                dispatch(activateUserRequest());
                const { accessToken } = state.signIn.user;
                await activateUser(uid, roleType, isDeactivate, accessToken);
                dispatch(activateUserSuccess());
            }
        } catch (error: any) {
            const [first] = error.response.data.statuses;
            dispatch(activateUserError({ errorMessage: first.message }));
        }
    };

export const updateUserRoles =
    (uid: string, roleType: number): AppThunk =>
    async (dispatch, getState) => {
        try {
            const state = getState();
            if (state.signIn.user) {
                dispatch(updateRolesRequest());
                const { accessToken } = state.signIn.user;
                await updateRoles(uid, roleType, accessToken);

                dispatch(updateRolesSuccess());

                dispatch(getUserAccountData(uid, roleType));
            }
        } catch (error: any) {
            const [first] = error.response.data.statuses;
            dispatch(updateRolesError({ errorMessage: first.message }));
        }
    };

export const getProfile = (): AppThunk => async dispatch => {
    try {
        const session = sessionCache().getSession();
        const accessToken = session?.accessToken;

        if (accessToken) {
            dispatch(getProfileRequest());

            const result = await getMyAccount(accessToken);
            const profile = mapUserProfile(result.data.account);

            dispatch(getProfileSuccess({ profile: profile }));
            buildAbilityFor(profile.roleType?.value ?? 0);
            if (session) {
                sessionCache().saveSession({
                    ...session,
                    roleType: profile.roleType ?? { value: 0, label: "" },
                });
            }

            dispatch(getExpertAreas());
            dispatch(getMeasures());
        }
    } catch (error: any) {
        const [first] = error.response.data.statuses;
        dispatch(getProfileError({ errorMessage: first }));
    }
};

export const updateProfile =
    (notificationType: number[], infoType: number[], moduleName: number[]): AppThunk =>
    async dispatch => {
        try {
            const session = sessionCache().getSession();
            const accessToken = session?.accessToken;

            if (accessToken) {
                await updateMyAccountSettings(
                    { notificationType, infoType, moduleName },
                    accessToken
                );
                dispatch(updateProfileSuccess());
            }
        } catch (error) {
            dispatch(updateProfileError());
        }
    };

/** Update metadata type for correct payload */
export const updateAccountMetadata =
    (metadata: UpdateMyAccountMetadataRequest): AppThunk =>
    async dispatch => {
        try {
            const session = sessionCache().getSession();
            const accessToken = session?.accessToken;

            if (accessToken) {
                dispatch(updateProfileRequest());

                await updateMyAccountMetadata(metadata, accessToken);

                dispatch(updateProfileSuccess());

                // find solution for refetching profile for every profile update
                dispatch(getProfile());
            }
        } catch (error) {
            dispatch(updateProfileError());
        }
    };

export const updateMobileNumber =
    (request: UpdateMobileNumberRequest): AppThunk =>
    async dispatch => {
        try {
            const session = sessionCache().getSession();
            const accessToken = session?.accessToken;

            if (accessToken) {
                await updateMyAccountMobileNumber(request, accessToken);
                dispatch(updateProfileSuccess());
                toast.success("Promjene uspješno spremljene");
            }
        } catch (error) {
            dispatch(updateProfileError());
            toast.error("Problem prilikom spremanja podataka, pokušajte ponovno...");
        }
    };

export const getAccountDashboardData = (): AppThunk => async dispatch => {
    try {
        const session = sessionCache().getSession();
        const accessToken = session?.accessToken;

        if (accessToken) {
            dispatch(getDashboardDataRequest());

            const response = await getUserAccountDashboardData(accessToken);

            dispatch(getDashboardDataSuccess(response.data));
        }
    } catch (error: any) {
        const [first] = error.response.data.statuses;
        dispatch(getDashboardDataError({ errorMessage: first }));
        toast.error(first.message);
    }
};

export const deactivateAccount = (): AppThunk => async dispatch => {
    try {
        const session = sessionCache().getSession();
        const accessToken = session?.accessToken;

        if (accessToken) {
            dispatch(deactivateAccountRequest());

            await deleteMyAccount(accessToken);

            dispatch(deactivateAccountSuccess());

            dispatch(logOff());
        }
    } catch (error: any) {
        const [first] = error.response.data.statuses;
        dispatch(deactivateAccountError({ errorMessage: first }));
    }
};

export const updateModuleManagement =
    (moduleId: number): AppThunk =>
    async dispatch => {
        try {
            const session = sessionCache().getSession();
            const accessToken = session?.accessToken;

            if (accessToken) {
                await updateModule({ moduleId }, accessToken);
                dispatch(updateModuleManagementSuccess(moduleId));
            }
        } catch (error) {
            dispatch(updateProfileError());
        }
    };

export const updateNotificationSubscription =
    (category: NotificationSubscriptionCategory, subscriptionId: number): AppThunk =>
    async dispatch => {
        try {
            const session = sessionCache().getSession();
            const accessToken = session?.accessToken;

            if (accessToken) {
                await updateSubscription({ category, subscriptionId }, accessToken);
                dispatch(updateProfileSuccess());
            }
        } catch (error) {
            dispatch(updateProfileError());
        }
    };

export const getNotificationSubscriptionsForCategory =
    (category: NotificationSubscriptionCategory): AppThunk =>
    async () => {
        try {
            const session = sessionCache().getSession();
            const accessToken = session?.accessToken;

            if (accessToken) {
                const subscriptions = await getSubscriptionsForCategory(accessToken, category);
                return subscriptions;
            }
        } catch (error) {
            console.error(error);
        }
    };

export const getModuleManagementData = (): AppThunk => async () => {
    try {
        const session = sessionCache().getSession();
        const accessToken = session?.accessToken;

        if (accessToken) {
            const modules = await getModuleManagement(accessToken);
            return modules;
        }
    } catch (error) {
        console.error(error);
    }
};

export const getEducationTimeSubscription = (): AppThunk => async () => {
    try {
        const session = sessionCache().getSession();
        const accessToken = session?.accessToken;

        if (accessToken) {
            const educationTime = await getEducationTimeSub(accessToken);
            return educationTime;
        }
    } catch (error) {
        console.error(error);
    }
};

export const updateEducationTimeSubscription =
    (timeSub: TimeSubscription): AppThunk =>
    async () => {
        try {
            const session = sessionCache().getSession();
            const accessToken = session?.accessToken;
            if (accessToken) {
                await updateEducationTimeSub(accessToken, timeSub);
            }
        } catch (error) {
            console.error(error);
        }
    };

export const getModuleUsageData = (): AppThunk => async dispatch => {
    try {
        const session = sessionCache().getSession();
        const accessToken = session?.accessToken;

        if (accessToken) {
            dispatch(getModuleUsageDataRequest());
            const response = await getModuleUsageDashboardData(accessToken);
            dispatch(getModuleUsageDataSuccess(response.data));
        }
    } catch (error: any) {
        const [first] = error.response.data.statuses;
        dispatch(getModuleUsageDataError({ errorMessage: first.message }));
        toast.error(first.message);
    }
};
